import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/InvoiceItemsService';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

export const useInvoiceItems = () => {
    const { invoiceId } = useParams();
    
    if (!invoiceId) {
        throw new Error('invoiceId is required parameter');
    }

    const queryClient = useQueryClient();
    const [limit] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [selection] = useState<Record<string, boolean>>({});
    
    const sort = { column: 'created', direction: 'desc' as const };
    
    // INFINITE QUERY
    const invoiceItemsQuery = useInfiniteQuery({
        queryKey: ['invoice-items', invoiceId, keyword, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({ limit, sort, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(invoiceId, id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoice-items', invoiceId] });
        }
    });

    const fetchRecordsAction = () => {
        if (invoiceItemsQuery.hasNextPage) {
            invoiceItemsQuery.fetchNextPage();
        }
    };

    const reloadRecords = () => {
        queryClient.invalidateQueries({ queryKey: ['invoice-items', invoiceId] });
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = invoiceItemsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: invoiceItemsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: invoiceItemsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        reloadRecords,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};